import { Close, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { hostname } from "../../utils";

export default ({
  open,
  onClose,
  sequence,
  companyId,
  getToken,
}: {
  open: boolean;
  onClose: () => void;
  sequence: any;
  companyId: string;
  getToken: Function;
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [addressData, setAddressData] = useState<any>({});
  const [error, setError] = useState("");

  const handleClose = () => {
    onClose();
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const currentToken = await getToken();
    // Include address if any fields are filled
    const hasAddress = Object.values(addressData).some((v) => v);
    const memberData = {
      ...formData,
      ...(hasAddress ? { address: addressData } : {}),
    };

    axios
      .post(
        `${hostname}/sequences/${sequence.id}/members`,
        { companyId: companyId, member: memberData },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .then(() => {
        setFormData({});
        setAddressData({});
        handleClose();
      })
      .catch((error) => {
        if (error.response?.data?.message) {
          setError(error.response.data.message);
        } else {
          setError("Failed to add member. Please try again.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const onAddressChange = (e: any) => {
    setAddressData({
      ...addressData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Typography variant={"h6"}>Add Member: {sequence?.name}</Typography>
          <IconButton onClick={() => handleClose()}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={onChange}
            required
          />
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={onChange}
          />
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={onChange}
            required
          />
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={onChange}
          />
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="LinkedIn URL"
            name="linkedinUrl"
            value={formData.linkedinUrl}
            onChange={onChange}
            placeholder="https://www.linkedin.com/in/username"
            helperText="Required for LinkedIn actions in sequences"
          />

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Mailing Address (for Direct Mail)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    name="address"
                    value={addressData.address || ""}
                    onChange={onAddressChange}
                    placeholder="123 Main St"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    name="address2"
                    value={addressData.address2 || ""}
                    onChange={onAddressChange}
                    placeholder="Apt 4B"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={addressData.city || ""}
                    onChange={onAddressChange}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="State/Province"
                    name="state"
                    value={addressData.state || ""}
                    onChange={onAddressChange}
                    placeholder="NY"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Postal Code"
                    name="postalCode"
                    value={addressData.postalCode || ""}
                    onChange={onAddressChange}
                    placeholder="10001"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    name="country"
                    value={addressData.country || ""}
                    onChange={onAddressChange}
                    placeholder="US"
                    helperText="Defaults to US if not specified"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="User Data (string, json)"
            name="userData"
            multiline
            rows={4}
            value={formData.userData}
            onChange={onChange}
          />
        </DialogContent>
        <DialogActions>
          <Button variant={"text"} onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton loading={loading} variant={"contained"} type="submit">
            Create
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
